/**
 * StatementMultiUploadReviewDialog
 *
 * Lets the user review ONE OR MORE statement files for a SINGLE bank
 * account, then uploads them as one "statement set". The server parses
 * the files in parallel, joins them with the continuity engine (closing
 * must equal the next opening — no gaps, overlaps trimmed) and runs the
 * normal pipeline once on the merged stream.
 *
 * One account per upload, deliberately: mixing accounts in one review
 * would spawn independent pipelines with concurrent start-date gates and
 * gap banners. Users upload one account's statements at a time.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText, Landmark, X, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_MB = 25;

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function StatementMultiUploadReviewDialog({
  open,
  files,
  bankAccounts = [],
  onClose,
  onAllQueued,
  addJob,
}) {
  const [rows, setRows] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [queued, setQueued] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows((files || []).map((f) => ({
      file: f,
      error: f.size > MAX_FILE_MB * 1024 * 1024 ? `Exceeds ${MAX_FILE_MB} MB limit` : null,
    })));
    setAccountId('');
    setQueued(false);
  }, [open, files]);

  const validRows = useMemo(() => rows.filter((r) => !r.error), [rows]);
  const validCount = validRows.length;
  const readyToUpload = validCount > 0 && !!accountId;

  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const handleStart = async () => {
    if (!readyToUpload || uploading) return;
    setUploading(true);
    const isBatch = validCount > 1;
    try {
      const fd = new FormData();
      if (isBatch) validRows.forEach((r) => fd.append('files', r.file));
      else fd.append('file', validRows[0].file);
      fd.append('bank_account_id', accountId);
      const res = await axios.post(`${API}/bank-feeds/${isBatch ? 'upload-batch' : 'upload-background'}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const acct = bankAccounts.find((a) => a.id === accountId) || {};
      addJob?.({
        jobId: res.data.job_id,
        filename: isBatch ? `Statement set (${validCount} files)` : validRows[0].file.name,
        bankAccountId: accountId,
        bankAccountName: acct.bank_name || acct.account_name || '',
        status: res.data.status || 'queued',
        progress: 5,
        statusMessage: isBatch ? `Reading ${validCount} statements in parallel` : 'Queued for parsing',
        totalTx: 0,
        processedTx: 0,
      });
      setQueued(true);
      toast.success(
        isBatch
          ? `${validCount} statements are being read together — they will be joined and checked for gaps before anything is imported`
          : '1 statement queued for processing',
      );
      onAllQueued?.();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || detail?.message || err?.message || 'Upload failed';
      toast.error(msg);
      setRows((prev) => prev.map((r) => (r.error ? r : { ...r, error: msg })));
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !uploading && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="multi-upload-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#108981]" />
            Review statements ({rows.length})
          </DialogTitle>
          <DialogDescription>
            All files in this upload must belong to the same bank account. They are read in parallel,
            joined month-to-month with balance checks (closing must equal the next opening — no gaps,
            overlaps trimmed) and imported as one set. Upload another account&apos;s statements separately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-medium">
              Bank account <span className="text-red-500">*</span>
            </Label>
            <Select value={accountId} onValueChange={setAccountId} disabled={uploading}>
              <SelectTrigger data-testid="bulk-bank-account-select">
                <SelectValue placeholder="Choose the account these statements belong to" />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    <div className="flex items-center gap-2">
                      <Landmark className="w-4 h-4 text-muted-foreground" />
                      <span>{a.bank_name || 'Bank'}</span>
                      <span className="text-muted-foreground">-</span>
                      <span>{a.account_name}</span>
                      {a.account_number && (
                        <span className="text-xs text-muted-foreground">
                          (***{String(a.account_number).slice(-4)})
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
            {rows.map((row, idx) => (
              <div
                key={`${row.file.name}-${idx}`}
                className={`flex items-center gap-3 p-3 border rounded-lg ${
                  row.error ? 'border-red-200 bg-red-50' : queued ? 'border-emerald-200 bg-emerald-50' : 'bg-white'
                }`}
                data-testid={`upload-review-row-${idx}`}
              >
                <FileText className="w-8 h-8 text-[#108981] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{row.file.name}</p>
                  <p className="text-xs text-muted-foreground">{formatSize(row.file.size)}</p>
                  {row.error && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {row.error}
                    </p>
                  )}
                  {queued && !row.error && (
                    <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Queued for parsing
                    </p>
                  )}
                </div>
                {!uploading && !queued && (
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="text-muted-foreground hover:text-red-600 p-1"
                    aria-label="Remove file"
                    data-testid={`upload-review-remove-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {uploading && (
            <p className="text-xs text-muted-foreground text-center" data-testid="upload-review-progress">
              Uploading {validCount} file{validCount === 1 ? '' : 's'}…
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose?.()} disabled={uploading} data-testid="upload-review-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleStart}
            disabled={!readyToUpload || uploading}
            className="bg-[#108981] hover:bg-[#0d7168]"
            data-testid="upload-review-start"
          >
            {uploading
              ? 'Uploading…'
              : validCount > 1 ? `Upload ${validCount} statements as one set` : `Upload ${validCount} statement`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
